import React, { useCallback, useEffect, useRef } from 'react';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { cn } from '@/utils/styles';

interface LazyTransitionProps {
  children: React.ReactElement;
  className?: string;
  animationClassName?: string;
  animationDuration?: number; // in milliseconds
  delay?: number; // in milliseconds
  onAnimationEnd?: () => void;
}

const LazyTransition = ({
  children,
  animationClassName = 'animate-fadeIn',
  animationDuration = 1000,
  delay = 0,
  onAnimationEnd,
}: LazyTransitionProps) => {
  const { inView, ref } = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true,
  });
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (inView) {
      containerRef.current?.classList.add(...animationClassName.split(' '));
    }
  }, [inView]);

  const setRefs = useCallback((node: HTMLDivElement) => {
    ref(node);
    containerRef.current = node;
  }, []);

  const lazyElement = React.cloneElement(children, {
    className: cn(children.props.className, 'opacity-0'),
    ref: setRefs,
    style: {
      ...children.props.style,
      animationFillMode: 'forwards',
      animationDelay: `${delay}ms`,
      animationDuration: `${animationDuration}ms`,
    },
    onAnimationEnd: () => onAnimationEnd?.(),
  });

  return lazyElement;
};

export default LazyTransition;
